import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, ArrowUpRight, Loader2 } from 'lucide-react';
import ContentLayout, { H2 } from '../components/ContentLayout';
import { useToast } from '../hooks/use-toast';

const PHONE = '+1 214 395 6757';
const EMAIL = 'friendsdesk@oshouniversal.org';

const LOCATIONS = [
  { name: 'Osho Kaivalya Dham (HQ)', address: '1642 FM 1310, Denison, TX 75020' },
];

const ContactUs = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: 'Please fill in required fields', description: 'Name, email and contact number are required.' });
      return;
    }
    setSending(true);
    setTimeout(() => {
      setSending(false);
      toast({ title: 'Message sent ✨', description: `Thank you ${form.name}, we’ll be in touch soon.` });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 900);
  };

  return (
    <ContentLayout
      testid="page-contacts"
      eyebrow="Contact"
      titleTop="Get in"
      titleItalic="Touch"
      intro="We’re here to walk alongside you on your journey. Whether you have questions about our programs, want to book a retreat, or simply need guidance — our team is ready to help."
    >
      <div className="grid md:grid-cols-2 gap-10">
        {/* Contact info */}
        <div className="space-y-4">
          <H2 top="Contact" italic="Information" />
          <a href={`tel:${PHONE.replace(/\s/g, '')}`} data-testid="contact-phone" className="flex items-center gap-3 p-4 rounded-2xl bg-white/70 border border-rose/40 hover:shadow-md transition-shadow">
            <span className="w-10 h-10 rounded-full bg-rose-soft flex items-center justify-center"><Phone className="w-5 h-5 text-mahogany" /></span>
            <span><span className="block text-xs uppercase tracking-wider text-mahogany/60">Give us a call</span><span className="text-burgundy font-medium">{PHONE}</span></span>
          </a>
          <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="flex items-center gap-3 p-4 rounded-2xl bg-white/70 border border-rose/40 hover:shadow-md transition-shadow">
            <span className="w-10 h-10 rounded-full bg-rose-soft flex items-center justify-center"><Mail className="w-5 h-5 text-mahogany" /></span>
            <span><span className="block text-xs uppercase tracking-wider text-mahogany/60">Email us</span><span className="text-burgundy font-medium break-all">{EMAIL}</span></span>
          </a>
          <div className="flex items-center gap-3 p-4 rounded-2xl bg-white/70 border border-rose/40">
            <span className="w-10 h-10 rounded-full bg-rose-soft flex items-center justify-center"><Clock className="w-5 h-5 text-mahogany" /></span>
            <span><span className="block text-xs uppercase tracking-wider text-mahogany/60">Open</span><span className="text-burgundy font-medium">Monday – Sunday, 9am – 7pm EST</span></span>
          </div>

          <div className="pt-2">
            <h3 className="font-serif-display text-2xl text-burgundy mb-3">All Locations</h3>
            <div className="space-y-3">
              {LOCATIONS.map((l) => (
                <div key={l.name} className="flex items-start gap-3 p-4 rounded-2xl bg-cream-2/60 border border-rose/40">
                  <MapPin className="w-4 h-4 text-rose-italic mt-1 shrink-0" />
                  <div><div className="text-burgundy font-medium">{l.name}</div><div className="text-sm text-mahogany/75">{l.address}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Form */}
        <div>
          <H2 top="Ask a" italic="Question" />
          <p className="text-mahogany/75 text-sm mb-5">If you have any questions, please fill out the form below and we’ll get back to you.</p>
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <Field label="Name *" name="name" value={form.name} onChange={set} testid="contact-name" />
            <Field label="E-Mail *" name="email" type="email" value={form.email} onChange={set} testid="contact-email-input" />
            <Field label="Contact No. *" name="phone" value={form.phone} onChange={set} testid="contact-phone-input" />
            <Field label="Subject" name="subject" value={form.subject} onChange={set} testid="contact-subject" />
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Your Message</span>
              <textarea name="message" rows={4} value={form.message} onChange={set} data-testid="contact-message"
                className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" />
            </label>
            <button type="submit" disabled={sending} className="pill-btn" data-testid="contact-submit">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Message <ArrowUpRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </div>
    </ContentLayout>
  );
};

const Field = ({ label, name, value, onChange, type = 'text', testid }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
    <input type={type} name={name} value={value} onChange={onChange} data-testid={testid}
      className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" />
  </label>
);

export default ContactUs;
